import { Node } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

import { VALID_IMAGE_TYPES, validateImageFile } from "@/utils/validateImageFile";

import { insertFileAttachment } from "./utils/insertFileAttachment";
import { insertImage } from "./utils/insertImage";
import { documentScope } from "./utils/uploadScope";

import type { UploadScope } from "./utils/uploadScope";
import type { DocumentModel } from "@/models/DocumentModel";

export interface DropImageOptions {
  doc: DocumentModel;
  uploadScope?: UploadScope;
}

export const DropImage = Node.create<DropImageOptions>({
  name: "dropImage",

  addProseMirrorPlugins() {
    const { doc, uploadScope } = this.options;
    return [
      new Plugin({
        key: new PluginKey("imageDrop"),
        props: {
          handleDrop(view, event, _slice, moved) {
            const file = event.dataTransfer?.files[0];

            if (!doc?.id || !file || moved) {
              return false;
            }

            event.preventDefault();

            const coords = { left: event.clientX, top: event.clientY };
            const originalPosition = view.posAtCoords(coords)?.pos;

            if (originalPosition === undefined) {
              return false;
            }

            let position = originalPosition;

            // Don't insert image before the document title
            view.state.doc.descendants((node, pos) => {
              if (node.type.name === "documentTitle") {
                position = Math.max(position, pos + node.nodeSize);
                return false;
              }
              return true;
            });

            const isImage = VALID_IMAGE_TYPES.includes(file.type);
            const scope = uploadScope ?? documentScope(doc.id);

            if (isImage) {
              if (!validateImageFile(file)) return false;

              insertImage(view, position, file, scope);
              return true;
            }

            return insertFileAttachment(view, position, file, scope);
          },
        },
      }),
    ];
  },
});
